import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, ClipboardList, CalendarDays } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AvailabilityManager from "@/components/AvailabilityManager";
import { BookingCard } from "@/components/BookingCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const TABS = [
  { v: "all", l: "Toutes" },
  { v: "en_attente", l: "En attente" },
  { v: "valide", l: "Validées" },
  { v: "confirme", l: "Confirmées" },
  { v: "refuse", l: "Refusées" },
];

export default function ArtistDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [view, setView] = useState("requests");
  const [dialog, setDialog] = useState(null); // {booking, status}
  const [msg, setMsg] = useState("");

  const endpoint = isAdmin ? "/bookings" : "/bookings/artist";

  const load = () => {
    setLoading(true);
    api.get(endpoint).then((r) => setBookings(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const act = async (booking, status, withMessage = false) => {
    if (withMessage) {
      setDialog({ booking, status });
      setMsg("");
      return;
    }
    try {
      await api.put(`/bookings/${booking.id}`, { status, response_message: "" });
      toast.success("Statut mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const confirmDialog = async () => {
    try {
      await api.put(`/bookings/${dialog.booking.id}`, { status: dialog.status, response_message: msg });
      toast.success("Réponse envoyée");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const filtered = tab === "all" ? bookings : bookings.filter((b) => b.status === tab);

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-14">
      <p className="text-[#C9A24B] text-xs tracking-[0.3em] uppercase mb-2">Espace artiste</p>
      <h1 className="font-serif text-4xl text-white mb-1">Demandes de réservation</h1>
      <p className="text-[#A19E95] mb-6">Validez ou refusez les demandes, envoyez vos devis et gérez votre calendrier.</p>

      <div className="flex gap-2 mb-8 border-b border-white/10">
        <button
          data-testid="view-requests"
          onClick={() => setView("requests")}
          className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition ${
            view === "requests" ? "border-[#C9A24B] text-white" : "border-transparent text-[#A19E95] hover:text-white"
          }`}
        >
          <ClipboardList size={16} /> Demandes
        </button>
        <button
          data-testid="view-availability"
          onClick={() => setView("availability")}
          className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition ${
            view === "availability" ? "border-[#C9A24B] text-white" : "border-transparent text-[#A19E95] hover:text-white"
          }`}
        >
          <CalendarDays size={16} /> Mon calendrier
        </button>
      </div>

      {view === "availability" && <AvailabilityManager />}

      {view === "requests" && (
      <>
      <div className="flex flex-wrap gap-2 mb-8">
        {TABS.map((t) => (
          <button
            key={t.v}
            data-testid={`artist-tab-${t.v}`}
            onClick={() => setTab(t.v)}
            className={`text-sm px-4 py-2 rounded-full border transition ${
              tab === t.v ? "border-[#C9A24B] bg-[#C9A24B]/10 text-white" : "border-white/10 text-[#A19E95] hover:border-[#C9A24B]/40"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-[#A19E95] text-center py-20">Chargement...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 border border-white/10 rounded-2xl" data-testid="artist-empty">
          <Inbox className="text-[#C9A24B] mx-auto mb-4" size={40} />
          <p className="text-white font-serif text-2xl">Aucune demande dans cette catégorie</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((b) => (
            <BookingCard key={b.id} b={b}>
              <div className="mt-4 pt-4 border-t border-white/5 text-sm">
                <p className="text-[#A19E95]">
                  <span className="text-[#6E6B63]">Client :</span> {b.client_name} · {b.client_email}
                  {b.contact_phone ? ` · ${b.contact_phone}` : ""}
                </p>
                {b.logistics_needed && (
                  <p className="text-[#A19E95] mt-1">
                    <span className="text-[#6E6B63]">Logistique :</span>{" "}
                    {Object.entries(b.logistics || {}).filter(([, v]) => v).map(([k]) => k).join(", ") || "à préciser"}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button size="sm" data-testid="action-valider" onClick={() => act(b, "valide", true)} className="gold-bg text-black font-semibold rounded-full">
                    Valider + devis
                  </Button>
                  <Button size="sm" data-testid="action-confirmer" onClick={() => act(b, "confirme")} className="bg-[#2EE59D]/20 text-[#2EE59D] border border-[#2EE59D]/40 rounded-full hover:bg-[#2EE59D]/30">
                    Confirmer
                  </Button>
                  <Button size="sm" variant="ghost" data-testid="action-refuser" onClick={() => act(b, "refuse", true)} className="text-[#FF4D4D] hover:bg-[#FF4D4D]/10 rounded-full">
                    Refuser
                  </Button>
                </div>
              </div>
            </BookingCard>
          ))}
        </div>
      )}
      </>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-[#121216] border-[#C9A24B]/25 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {dialog?.status === "refuse" ? "Refuser la demande" : "Valider et envoyer un devis"}
            </DialogTitle>
            <DialogDescription className="text-[#A19E95]">
              {dialog?.status === "refuse"
                ? "Indiquez au client la raison du refus."
                : "Ajoutez les détails de votre devis avant d'envoyer votre réponse au client."}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="response-message"
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            placeholder={dialog?.status === "refuse" ? "Expliquez la raison du refus..." : "Détails du devis, montant, conditions..."}
            rows={4}
            className="bg-[#181820] border-white/10 text-white"
          />
          <Button onClick={confirmDialog} data-testid="send-response-btn" className="gold-bg text-black font-semibold rounded-full">
            Envoyer la réponse
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
